import dayjs from "dayjs";
import LogoImage from "../Images/home/logo.webp";

// Get coin by network
export const getCoinByNetwrok = (network = "") => {
    switch (network) {
        case "ERC20":
            return "ETH";
        case "TRC20":
            return "TRX";
        default:
            return "BTC";
    }
}

export const isValidCryptoAddress = (asset, address) => {
    const regexPatterns = {
        btc: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
        eth: /^0x[a-fA-F0-9]{40}$/,
        trx: /^T[A-Za-z1-9]{33}$/,
        pol: /^0x[a-fA-F0-9]{40}$/, // Polygon uses Ethereum addresses
        usdt: /^0x[a-fA-F0-9]{40}$/,
        usdc: /^0x[a-fA-F0-9]{40}$/,
        matic: /^0x[a-fA-F0-9]{40}$/
    };

    return regexPatterns[asset.toLowerCase()]?.test(address) || false;
};

export const formattedValue = (value) => {
    const formattedMaxValue = new Intl.NumberFormat('en-US').format(value);
    return formattedMaxValue

}

export function maskEmail(email = '') {
    const [localPart, domain] = email.split('@');

    if (localPart.length <= 2) {
        return `${localPart[0]}***@${domain}`;
    }

    const first = localPart.slice(0, 2);
    const dots = '.'.repeat(localPart.length - 2);
    return `${first}${dots}@${domain}`;
}

const currencyToCountry = {
    USD: "us", // United States
    EUR: "eu", // European Union
    GBP: "gb", // United Kingdom
    CHF: "ch", // Switzerland
    JPY: "jp", // Japan
    AUD: "au", // Australia
    CAD: "ca", // Canada
    NZD: "nz", // New Zealand
    CNY: "cn", // China
    HKD: "hk", // Hong Kong
    SGD: "sg", // Singapore
    INR: "in", // India
    RUB: "ru", // Russia
    BRL: "br", // Brazil
    ZAR: "za", // South Africa
    KRW: "kr", // South Korea
    SEK: "se", // Sweden
    NOK: "no", // Norway
    DKK: "dk", // Denmark
    MXN: "mx", // Mexico
    TRY: "tr", // Turkey
    PLN: "pl", // Poland
    HUF: "hu", // Hungary
    CZK: "cz", // Czech Republic
    SAR: "sa", // Saudi Arabia
    AED: "ae", // United Arab Emirates
    THB: "th", // Thailand
    IDR: "id", // Indonesia
    MYR: "my", // Malaysia
    PHP: "ph", // Philippines
    TWD: "tw", // Taiwan
};

export const getFlagUrlsFromPair = (pair = "EURUSD") => {
    if (typeof pair !== "string" || pair.length !== 6) {
        return {
            baseImg: getPairImgUrl("EUR"),
            quoteImg: getPairImgUrl("USD"),
        };
    }

    const baseCurrency = pair.slice(0, 3).toUpperCase();
    const quoteCurrency = pair.slice(3, 6).toUpperCase();

    return {
        baseImg: getPairImgUrl(baseCurrency),
        quoteImg: getPairImgUrl(quoteCurrency),
    };
};

export const handleImageError = (e, fallBackImage = null) => {
    e.target.onerror = null; // prevent infinite loop if fallback also fails
    e.target.src = fallBackImage || LogoImage; // fallback image
}

export function formatCurrency(amount, locale = "en-IN", currency = "USD") {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return "0";
    }
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(amount);
}

export const getPairImgUrl = (currencyCode) => currencyToCountry[currencyCode.toUpperCase()] ? `https://flagcdn.com/48x36/${currencyToCountry[currencyCode.toUpperCase()]}.png` : `https://flagcdn.com/48x36/us.png`;
export const getCountryImage = (currencyCode) => `https://flagcdn.com/48x36/${currencyCode?.toLowerCase()}.png`;

export async function getFlagURL(countryName, width = 48, height = 36) {
    // if (!countryName || typeof countryName !== "string") {
    //     console.error("Invalid country name:", countryName);
    //     return "Country name is required.";
    // }

    try {
        const response = await fetch(`https://restcountries.com/v3.1/name/${String(countryName)}`);
        if (!response.ok) throw new Error("Country not found");

        const data = await response.json();
        const countryCode = data[0]?.cca2?.toLowerCase();

        if (!countryCode) throw new Error("ISO code not found");

        return `https://flagcdn.com/${width}x${height}/${countryCode}.png`;
    } catch (error) {
        console.error("Error fetching flag:", error.message);
        return "Country not found.";
    }
}

export function getTradingViewSymbol(symbol = '') {

    const cleanSymbol = symbol.replace(/_$/, "").toUpperCase();

    // Forex
    const forexPairs = ["EURUSD", "GBPUSD", "USDCHF", "USDJPY", "USDNGN"];
    if (forexPairs.includes(cleanSymbol)) {
        return `OANDA:${cleanSymbol}`;
    }

    // Crypto (assuming Binance for now)
    const cryptoSymbols = ["BTCUSD", "ETHUSD", "LTCUSD", "XRPUSD", "BCHUSD", "DASHUSD"];
    if (cryptoSymbols.includes(cleanSymbol)) {
        return `BINANCE:${cleanSymbol}`;
    }

    // Commodities / Metals
    if (cleanSymbol === "XAUUSD" || cleanSymbol === "XAUUSD.LM") {
        return "TVC:GOLD";
    }
    if (cleanSymbol === "WTIUSD") {
        return "TVC:USOIL";
    }
    if (cleanSymbol === "BRNUSD") {
        return "TVC:UKOIL";
    }
    if (cleanSymbol === "NGCUSD") {
        return "TVC:NATGASUSD";
    }

    const indexMap = {
        SP500: "SP:SPX",               // S&P 500
        NAS100: "NASDAQ:NDX",          // Nasdaq 100
        JPN225: "TVC:JP225",           // Nikkei 225
        UK100: "TVC:UKX",              // FTSE 100
        ESX50: "INDEXEURO:STOX50E",    // Euro Stoxx 50
        WS30: "DJ:DJI",                // Dow Jones
    };

    if (indexMap[cleanSymbol]) {
        return indexMap[cleanSymbol];
    }


    // Fallback
    return `OANDA:${cleanSymbol}`;
}

export const isEmpty = value =>
    value === undefined ||
    value === null ||
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (typeof value === "string" && value?.trim().length === 0);

export const isBoolean = value => (typeof value === "boolean" && (value === true || value === false)) || (typeof value === "string" && (value === "true" || value === "false"));

export const isEmptyObject = obj =>
    Object.values(obj).every(
        value => value === null || value === undefined || value === ""
    );

export const toFixedDown = (item, type = 2) => {
    try {
        if (!isEmpty(item) && !isNaN(item)) {
            item = parseFloat(item);
            let decReg = new RegExp("(\\d+\\.\\d{" + type + "})(\\d)"),
                m = item.toString().match(decReg);
            return m ? parseFloat(m[1]) : item.valueOf();
        }
        return "";
    } catch (err) {
        return "";
    }
};

export const toFixed = (item, type = 8) => {
    try {
        if (!isEmpty(item) && !isNaN(item)) {
            const num = parseFloat(item);
            return parseFloat(num.toFixed(type)).toString();
        }
        return "";
    } catch (err) {
        return "";
    }
};


export function getSignalFromDir(bidDir, askDir) {
    // Use BidDir primarily for signal
    if (bidDir === "1") return "up";
    if (bidDir === "-1") return "down";
    return "-"; // neutral
}

export function calculateOneDayChange(data) {
    const open = parseFloat(data.PriceOpen);
    const bid = parseFloat(data.Bid);

    if (isNaN(open) || isNaN(bid) || open === 0) {
        return { change: NaN, percentChange: NaN };
    }

    const change = bid - open;
    const percentChange = (change / open) * 100;

    return { change, percentChange };
}

export const timeStampFormat = timestamp => {
    try {
        const newTimestamp = timestamp - 180 * 60 * 1000;
        const newDate = new Date(newTimestamp);
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        const hours = String(newDate.getHours()).padStart(2, "0");
        const minutes = String(newDate.getMinutes()).padStart(2, "0");
        const seconds = String(newDate.getSeconds()).padStart(2, "0");
        const formattedDate = `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
        return formattedDate;
    } catch (err) {
        return "";
    }
};

export const convert = n => {
    try {
        var sign = +n < 0 ? "-" : "",
            toStr = n.toString();
        if (!/e/i.test(toStr)) {
            return n;
        }
        var [lead, decimal, pow] = n
            .toString()
            .replace(/^-/, "")
            .replace(/^([0-9]+)(e.*)/, "$1.$2")
            .split(/e|\./);
        return +pow < 0
            ? sign +
            "0." +
            "0".repeat(Math.max(Math.abs(pow) - 1 || 0, 0)) +
            lead +
            decimal
            : sign +
            lead +
            (+pow >= decimal.length
                ? decimal + "0".repeat(Math.max(+pow - decimal.length || 0, 0))
                : decimal.slice(0, +pow) + "." + decimal.slice(+pow));
    } catch (err) {
        return 0;
    }
};

export const truncateDecimals = (num, decimals) => {
    num = convert(num);
    let s = num.toString(),
        p = s.indexOf(".");
    s += (p < 0 ? ((p = 1 + s.length), ".") : "") + "0".repeat(decimals);
    return s.slice(0, p + 1 + decimals);
};

export const formatDate = (date) => {
    // Split the input duration like "1min:12secs" into components
    const formattedDate = dayjs(date).format("MMMM D, hh:mm A");

    return formattedDate;
};

export const convertMillisecondsToDate = (time) => {
    const timestamp = time;
    const date = new Date(timestamp);

    // Format options
    const options = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };

    const formattedDate = date.toLocaleString('en-US', options);

    return formattedDate;
};

export function timeAgoConvert(dateStr) {
    const now = new Date();
    const past = new Date(dateStr);
    const seconds = Math.floor((now - past) / 1000); // Always positive, past-oriented

    const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'week', seconds: 604800 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
        { label: 'second', seconds: 1 },
    ];

    for (const interval of intervals) {
        const count = Math.floor(seconds / interval.seconds);
        if (count >= 1) {
            return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
        }
    }

    return 'just now';
}

export const navigateExternalPage = (link = process.env.REACT_APP_FRONTEND_URL) => {
    window.open(link, "_blank");
};

export const validateWalletAddress = (address = "", currency = "") => {
    const regexes = {
        BTC: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/, // Bitcoin (Legacy and P2PKH)
        BTC_SEGWIT: /^(bc1)[a-zA-HJ-NP-Z0-9]{39,59}$/, // Bitcoin SegWit
        ETH: /^0x[a-fA-F0-9]{40}$/, // Ethereum (ERC20, BEP20, MATIC20)
        TRX: /^T[a-zA-Z0-9]{33}$/, // TRON (TRC20)
        XRP: /^r[0-9a-zA-Z]{24,34}$/, // Ripple
        LTC: /^(L|M|ltc1)[a-km-zA-HJ-NP-Z1-9]{26,40}$/, // Litecoin
        DOGE: /^D{1}[5-9A-HJ-NP-U]{1}[1-9A-HJ-NP-Za-km-z]{32}$/, // Dogecoin
        SOL: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/, // Solana
        DOT: /^1[a-zA-HJ-NP-Z0-9]{47}$/, // Polkadot
        ADA: /^addr[a-z0-9]{58,76}$/, // Cardano
        BEP2: /^bnb[a-hj-np-z0-9]{39}$/, // Binance BEP2
    };

    const upperCurrency = currency.toUpperCase();

    switch (upperCurrency) {
        case "BTC":
            return regexes.BTC.test(address) || regexes.BTC_SEGWIT.test(address);
        case "ETH":
        case "ERC20":
        case "BEP20":
        case "MATIC":
            return regexes.ETH.test(address);
        case "TRX":
        case "TRC20":
            return regexes.TRX.test(address);
        case "XRP":
            return regexes.XRP.test(address);
        case "LTC":
            return regexes.LTC.test(address);
        case "DOGE":
            return regexes.DOGE.test(address);
        case "SOL":
            return regexes.SOL.test(address);
        case "DOT":
            return regexes.DOT.test(address);
        case "ADA":
            return regexes.ADA.test(address);
        case "BEP2":
            return regexes.BEP2.test(address);
        default:
            return false;
    }
};


const maxFileSize = 1 * 1024 * 1024; // 1MB
const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

export const isValidImage = (file, label, key, setFormErrors, allowed_Types = allowedTypes, max_file_size = maxFileSize) => {
    let errors = {};

    if (!allowed_Types.includes(file.type)) {
        errors[key] = `${label} must be JPEG, JPG, or PNG format.`;
    } else if (file.size > max_file_size) {
        errors[key] = `${label} must be less than 1 MB.`;
    }

    setFormErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
};

export function formatNumberAbbreviation(value) {
    const abs = Math.abs(Number(value));
    if (abs >= 1e12) {
        return (value / 1e12).toFixed(2) + 'T'; // Trillion
    } else if (abs >= 1e9) {
        return (value / 1e9).toFixed(2) + 'B'; // Billion
    } else if (abs >= 1e6) {
        return (value / 1e6).toFixed(2) + 'M'; // Million
    } else if (abs >= 1e3) {
        return (value / 1e3).toFixed(2) + 'K'; // Thousand
    } else {
        return Number(value).toFixed(2);
    }
}


export const getTimeAgo = (dateString) => {
    const now = new Date();
    const createdAt = new Date(dateString);
    const diffInSeconds = Math.floor((now - createdAt) / 1000);

    const minute = 60;
    const hour = 60 * minute;
    const day = 24 * hour;
    const month = 30 * day;
    const year = 365 * day;

    if (diffInSeconds < minute) {
        const s = diffInSeconds;
        return `${s} second${s !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < hour) {
        const m = Math.floor(diffInSeconds / minute);
        return `${m} minute${m !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < day) {
        const h = Math.floor(diffInSeconds / hour);
        return `${h} hour${h !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < month) {
        const d = Math.floor(diffInSeconds / day);
        return `${d} day${d !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < year) {
        const mo = Math.floor(diffInSeconds / month);
        return `${mo} month${mo !== 1 ? 's' : ''} ago`;
    }

    const y = Math.floor(diffInSeconds / year);
    return `${y} year${y !== 1 ? 's' : ''} ago`;
};
export const formatNumberShort = (value, decimals = 2) => {
    if (!value) return "0";

    const num = Number(value);

    if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(decimals) + "B";
    }
    if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(decimals) + "M";
    }
    if (num >= 1_000) {
        return (num / 1_000).toFixed(decimals) + "K";
    }

    return num.toFixed(decimals);
};
